import { useQuery } from '@tanstack/react-query';
import { getApiBaseUrl } from '../utils/apiBase';
import type { BatchMetricsResult } from '../context/StatCardsBatchContext';

interface UseStatCardsBatchProps {
  storeId: string | null;
  metricPaths: string[];
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

export function useStatCardsBatch({
  storeId,
  metricPaths,
  startDate,
  endDate,
  enabled = true,
}: UseStatCardsBatchProps) {
  const pathsKey = [...metricPaths].sort().join(',');

  return useQuery({
    queryKey: ['stat-cards-batch', storeId, startDate, endDate, pathsKey],
    queryFn: async (): Promise<BatchMetricsResult> => {
      if (!storeId || !startDate || !endDate || metricPaths.length === 0) {
        throw new Error('Missing batch params');
      }

      const params = new URLSearchParams({
        metrics: metricPaths.join(','),
        startDate,
        endDate,
        includePrevious: 'true',
      });

      const url = `${getApiBaseUrl()}/api/metrics/${storeId}/batch?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Batch request failed: ${response.status}`);
      }

      const json = await response.json();
      return json.data as BatchMetricsResult;
    },
    enabled: enabled && !!storeId && metricPaths.length > 0 && !!startDate && !!endDate,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}
